import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Redux
import {
  setCandidates,
  setJobMatchingStatus,
  setJobStatus,
  setNoRequirementsSet,
  setSelectedCVs,
  resetSelectedJobSlice,
} from "../redux/slices/jobSlice";
import { addOrUpdateMatch } from "../redux/slices/matchSlice";

// Hooks
import { useFetchMatchByJobId } from "./useFetchMatchByJobId";

// Constants
import { JOB_STATUS, JOB_MATCHING_STATUS } from "../constants";

/**
 * Custom hook that fetches job match data and updates the state.
 *
 * @param {string} jobId The job to get from store or fetch.
 * @param {string} selectedCandidate The selected candidate to load in the sidebar.
 * @returns {void}
 */
export const useJobInfo = (jobId, selectedCandidate) => {
  const dispatch = useDispatch();

  // eslint-disable-next-line no-unused-vars
  const [_, setSearchParams] = useSearchParams();

  const cvs = useSelector((state) => state.cv.cvs);

  const storedJob = useSelector((state) => state.jobs.jobs?.[jobId]);
  const selectedCVs = useSelector((state) => state.jobs.selectedCVs);

  const storedJobMatch = useSelector((state) => state.match.matches[jobId]);
  const jobMatchingStatus = useSelector((state) => state.jobs.jobMatchingStatus);

  const enableFetching =
    Boolean(jobId) && !storedJobMatch && [null, JOB_MATCHING_STATUS.MATCHING_IN_PROGRESS].includes(jobMatchingStatus);

  const { data: fetchedData, isFetching, isError } = useFetchMatchByJobId({
    jobId,
    enabled: enableFetching,
  });

  // Reset state when jobId changes
  useEffect(() => {
    if (!jobId) return;

    dispatch(resetSelectedJobSlice());
  }, [jobId, dispatch]);

  // Set selected CVs when storedJob changes
  useEffect(() => {
    if (storedJob?.selected_candidates?.length && !selectedCVs?.length) {
      dispatch(setSelectedCVs(storedJob.selected_candidates));
    }
  }, [storedJob, selectedCVs, dispatch]);

  // Handle fetching state
  useEffect(() => {
    if (isFetching || jobMatchingStatus === JOB_MATCHING_STATUS.NO_MATCHING_RESULT) {
      return;
    }

    if (isError) {
      return onError({ dispatch });
    }

    // Job match is available, update the state
    if (!jobMatchingStatus && storedJobMatch) {
      updateSelectedMatchState({ dispatch, matchData: storedJobMatch, selectedCandidate, setSearchParams });
      return;
    }

    // If fetched data is available, parse and update the state
    if (!storedJobMatch && fetchedData && jobMatchingStatus !== JOB_MATCHING_STATUS.MATCHED) {
      parseAndStoreMatchData({
        dispatch,
        matchData: {
          ...fetchedData,
          vacancy_id: jobId
        },
        cvs,
        selectedCandidate,
        setSearchParams
      });
      return;
    }
  }, [isFetching, isError, jobId, jobMatchingStatus, storedJobMatch, fetchedData, dispatch, cvs, selectedCandidate, setSearchParams]);

  return {
    isFetching
  }
};

// Parse fetched data and store it in the state
const parseAndStoreMatchData = ({ dispatch, matchData, cvs, selectedCandidate, setSearchParams }) => {
  const parsedMatchData = parseMatchData({ matchData, cvs });

  if (!parsedMatchData) {
    dispatch(setJobMatchingStatus(JOB_MATCHING_STATUS.MATCHING_IN_PROGRESS));
    return;
  }

  dispatch(addOrUpdateMatch(parsedMatchData));
  updateSelectedMatchState({ dispatch, matchData: parsedMatchData, selectedCandidate, setSearchParams });
}

// Handle error state
const onError = ({ dispatch }) => {
  dispatch(setJobMatchingStatus(JOB_MATCHING_STATUS.NO_MATCHING_RESULT));
  dispatch(setNoRequirementsSet(true));
}

// Update the sidebar state with the match data
const updateSelectedMatchState = ({dispatch, matchData, selectedCandidate, setSearchParams}) => {
  dispatch(setJobStatus(JOB_STATUS.COMPLETED));
  dispatch(setJobMatchingStatus(JOB_MATCHING_STATUS.MATCHED));
  dispatch(setSelectedCVs(matchData.candidatesPool));
  dispatch(setCandidates({ candidates: matchData.candidates }));

  setSelectedCandidate({ candidates: matchData.candidates, selectedCandidate, setSearchParams });
};

// Set the first candidate as the selected candidate if none is selected
const setSelectedCandidate = ({ candidates, selectedCandidate, setSearchParams = () => {}}) => {
  if (!candidates?.length) return;
  
  const firstCandidateId = candidates[0].id;
  const candidateExists = selectedCandidate && candidates.some(c => c.id === selectedCandidate);

  if (!candidateExists) {
    setSearchParams({ candidate: firstCandidateId });
  }
};

const parseMatchData = ({ matchData, cvs }) => {
  if (!matchData) return;

  // Map candidate data to include CVs
  let parsedCandidates = matchData.candidates
    ?.map((candidate) => ({
      ...candidate,
      cv: cvs?.find(({ id }) => id === candidate.person_id),
      id: candidate.person_id,
    }))
    // Sort by score in descending order
    .sort((a, b) => b.summary_score - a.summary_score) ?? [];

  // TODO: Check if this is necessary or be re-implemented in the future (data is not returning topK and threshold)
  // if (parsedCandidates.length) {
  //   const topK = matchData.job_meta?.actual_topK;
  //   if (topK) {
  //     parsedCandidates = parsedCandidates.slice(0, topK);
  //   }
    
  //   // Always returning 3 candidates (Check if necessary or be re-implemented in the future)
  //   const threshold = matchData.job_meta?.threshold;
  //   if (threshold) {
  //     const newCandidates = parsedCandidates.filter(
  //       (candidate) => Math.round(candidate.summary_score) >= Number(threshold)
  //     );

  //     if (newCandidates.length < 3) {
  //       parsedCandidates = parsedCandidates.slice(0, 3);
  //     } else {
  //       parsedCandidates = newCandidates;
  //     }
  //   }
  // }

  return {
    ...matchData,
    candidates: parsedCandidates,
  };
};
